// frontend/src/pages/builder/DataSettingsPanel.jsx
// Phase 3 step 7b-1b: the properties panel's data settings -- sort, row limit,
// empty periods -- on the kit's Select and ToggleGroup (7c-2). Every control
// says what "automatic" currently means, so the chart's order and gaps are
// never a mystery. Rules live in dataSettings.js. 7d-2: for the Table type
// every column is a sort target and empty periods (a chart-shaping setting)
// are not offered; the table's headings change the same sort. 7e-3: for the
// Matrix, the fields are listed as Rows and Columns (`matrixRows` row fields
// first), Auto is the hierarchy A to Z (no sort is sent), and a value sort
// orders the groups at every level by their totals.
import React from 'react';
import { PanelHint, PanelLabel } from '@/components/ui/panel.jsx';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue,
} from '@/components/ui/select.jsx';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group.jsx';
import {
  DEFAULT_LIMIT, EMPTY_PERIOD_MODES, ROW_LIMITS, autoSort, effectiveSort, emptyPeriodsInfo, rankingNote,
  selectedSortKey, sortTargets,
} from './dataSettings';

function dirLabels(t) {
  if (!t) return ['Ascending', 'Descending'];
  if (t.kind === 'value') return ['Low to high', 'High to low'];
  return t.time ? ['Oldest first', 'Newest first'] : ['A to Z', 'Z to A'];
}

const sameTarget = (a, b) => (a.aggregation || null) === (b.aggregation || null) && (a.field || null) === (b.field || null);

export default function DataSettingsPanel({ spec, settings, fieldsByKey, onChange, type = 'bar', matrixRows = 0 }) {
  const isTable = type === 'table';
  const isMatrix = type === 'matrix';
  const targets = sortTargets(spec, fieldsByKey);
  const sortKey = selectedSortKey(spec, settings);
  // A matrix with no chosen sort sends none: the hierarchy A to Z.
  const sort = isMatrix ? (sortKey === 'auto' ? null : settings.sort) : effectiveSort(spec, settings);
  const current = sort ? targets.find((t) => sameTarget(t.target, sort.target)) : null;
  const auto = autoSort(spec);
  const autoTarget = auto ? targets.find((t) => sameTarget(t.target, auto.target)) : null;
  // The direction control beside it already shows the automatic direction.
  const autoLabel = isMatrix ? 'Auto · rows A to Z' : autoTarget ? `Auto · ${autoTarget.label}` : 'Auto';
  // A chart: the X axis, then the Group by fields. A table: all its grouping columns together.
  // A matrix: its row fields, then its column fields.
  const groupingTargets = targets.filter((t) => t.kind !== 'value');
  const xTargets = isTable ? groupingTargets : isMatrix ? groupingTargets.slice(0, matrixRows) : groupingTargets.filter((t) => t.kind === 'x');
  const groupByTargets = isTable ? [] : isMatrix ? groupingTargets.slice(matrixRows) : groupingTargets.filter((t) => t.kind === 'dimension');
  const [firstLabel, secondLabel] = isTable ? ['Columns', ''] : isMatrix ? ['Rows', 'Columns'] : ['X axis', 'Group by'];
  const valueTargets = targets.filter((t) => t.kind === 'value');
  const empty = emptyPeriodsInfo(spec, settings);
  const ranking = isMatrix ? null : rankingNote(spec, settings, fieldsByKey);
  const set = (patch) => onChange({ ...settings, ...patch });

  function onSortTarget(key) {
    if (key === 'auto') { set({ sort: null }); return; }
    const t = targets.find((x) => x.key === key);
    if (t) set({ sort: { target: t.target, dir: t.kind === 'value' ? 'desc' : 'asc' } });
  }

  return (
    <div className="flex flex-col">
      <PanelLabel id="builder-sort-label">Sort</PanelLabel>
      <div className="flex flex-col gap-2">
        <Select value={sortKey} onValueChange={onSortTarget} disabled={!targets.length}>
          <SelectTrigger aria-labelledby="builder-sort-label" aria-label="Sort by">
            <SelectValue placeholder={isTable ? 'Needs a grouping column' : isMatrix ? 'Needs a field on Rows' : 'Needs a field on the X axis'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">{autoLabel}</SelectItem>
            {xTargets.length > 0 && (
              <>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>{firstLabel}</SelectLabel>
                  {xTargets.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectGroup>
              </>
            )}
            {groupByTargets.length > 0 && (
              <SelectGroup>
                <SelectLabel>{secondLabel}</SelectLabel>
                {groupByTargets.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectGroup>
            )}
            {valueTargets.length > 0 && (
              <SelectGroup>
                <SelectLabel>Values</SelectLabel>
                {valueTargets.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
        <ToggleGroup type="single" aria-label="Sort direction" disabled={!sort}
          value={sort ? sort.dir : 'asc'} onValueChange={(v) => v && sort && set({ sort: { target: sort.target, dir: v } })}>
          <ToggleGroupItem value="asc">{dirLabels(current)[0]}</ToggleGroupItem>
          <ToggleGroupItem value="desc">{dirLabels(current)[1]}</ToggleGroupItem>
        </ToggleGroup>
      </div>

      <PanelLabel id="builder-limit-label">{isMatrix ? 'Row limit' : 'Rows'}</PanelLabel>
      <Select value={String(settings.limit || DEFAULT_LIMIT)}
        onValueChange={(v) => { const n = Number(v); set({ limit: n === DEFAULT_LIMIT ? null : n }); }}>
        <SelectTrigger aria-labelledby="builder-limit-label" aria-label="Rows"><SelectValue /></SelectTrigger>
        <SelectContent>
          {ROW_LIMITS.map((n) => (
            <SelectItem key={n} value={String(n)}>{n.toLocaleString()}{n === DEFAULT_LIMIT ? ' (default)' : ''}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {ranking && <PanelHint>{ranking}</PanelHint>}
      {isMatrix && <PanelHint>Counts every row the database returns, subtotals included.</PanelHint>}

      {empty.relevant && !isTable && !isMatrix && (
        <>
          <PanelLabel id="builder-empty-label">Empty periods</PanelLabel>
          <ToggleGroup type="single" aria-labelledby="builder-empty-label" value={empty.effective}
            onValueChange={(v) => v && set({ emptyPeriods: v })}>
            {EMPTY_PERIOD_MODES.map((m) => (
              <ToggleGroupItem key={m.id} value={m.id} disabled={m.id === 'zero' && !empty.zeroApplies}>{m.label}</ToggleGroupItem>
            ))}
          </ToggleGroup>
          <PanelHint>
            {!empty.zeroApplies ? 'Zero applies to counts and sums; averages, minimums and maximums have no value in an empty period.'
              : empty.effective === 'zero' ? 'Periods with no data count as 0.'
                : empty.effective === 'connect' ? 'Lines join across periods with no data.' : 'Periods with no data are left blank.'}
          </PanelHint>
        </>
      )}
    </div>
  );
}